import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Star, Search, Send, Flag, Archive, ArchiveRestore, MessageSquare, ChevronDown, Clock, User, Briefcase, AlertTriangle, Loader2, Link2, RefreshCw, MapPin, CheckCircle2, XCircle, Building2, ExternalLink, Info } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { marketingAPI } from '../lib/api';
import { Layout } from '../components/Layout';

// ── Stars renderer ──
const Stars = ({ rating, size = 'sm' }) => {
  const cls = size === 'lg' ? 'w-5 h-5' : 'w-3.5 h-3.5';
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(i => (
        <Star key={i} className={`${cls} ${i <= rating ? 'fill-amber-400 text-amber-400' : 'text-zinc-300'}`} />
      ))}
    </div>
  );
};

const STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  needs_response: { label: 'Needs Response', color: 'bg-orange-100 text-orange-800 border-orange-200' },
  replied: { label: 'Replied', color: 'bg-green-100 text-green-800 border-green-200' },
  flagged: { label: 'Flagged', color: 'bg-red-100 text-red-800 border-red-200' },
  archived: { label: 'Archived', color: 'bg-zinc-100 text-zinc-500 border-zinc-200' },
};

const PLATFORM_LABELS = { google: 'Google', yelp: 'Yelp', facebook: 'Facebook' };

// ── Platform icon ──
const PlatformIcon = ({ platform }) => {
  if (platform === 'google') return <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>;
  if (platform === 'yelp') return <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="#D32323"><path d="M12.59 16.48c-.18-.43-.27-.89-.27-1.36V2.87c0-.48.09-.94.27-1.36.52-1.22 1.93-1.53 2.92-.64l7.44 6.66c.99.89.74 2.46-.51 3.2l-4.68 2.78c-.78.47-1.76.47-2.55 0l-2.62-1.53z"/></svg>;
  if (platform === 'facebook') return <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>;
  return null;
};

// ── Stat Cards ──
const StatCards = ({ stats }) => {
  if (!stats) return null;
  const cards = [
    { label: 'Average Rating', value: stats.average_rating ? stats.average_rating.toFixed(1) : '—', sub: <Stars rating={Math.round(stats.average_rating || 0)} />, accent: 'border-l-amber-400' },
    { label: 'Total Reviews', value: stats.total_reviews, accent: 'border-l-blue-400' },
    { label: 'New (30 days)', value: stats.new_reviews_30d, accent: 'border-l-emerald-400' },
    { label: 'Awaiting Response', value: stats.awaiting_response, accent: stats.awaiting_response > 0 ? 'border-l-red-400' : 'border-l-zinc-300', alert: stats.awaiting_response > 0 },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="review-stats">
      {cards.map(c => (
        <Card key={c.label} className={`border-l-4 ${c.accent}`}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground font-medium">{c.label}</p>
              {c.alert && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" data-testid="awaiting-response-dot" />}
            </div>
            <p className={`text-2xl font-bold mt-0.5 ${c.alert ? 'text-red-600' : ''}`}>{c.value}</p>
            {c.sub && <div className="mt-0.5">{c.sub}</div>}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ── Review List Item ──
const ReviewListItem = ({ review, isSelected, onClick }) => {
  const st = STATUS_CONFIG[review.is_archived ? 'archived' : review.status] || STATUS_CONFIG.new;
  const isUrgent = review.rating <= 2 && !review.is_archived;
  const needsAttention = ['new', 'needs_response'].includes(review.status) && !review.is_archived;
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-3 border-b transition-colors ${isSelected ? 'bg-blue-50 border-l-2 border-l-blue-500' : 'hover:bg-zinc-50 border-l-2 border-l-transparent'} ${isUrgent ? 'bg-red-50/40' : ''} ${review.is_archived ? 'opacity-60' : ''}`}
      data-testid={`review-item-${review.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-0.5">
            <span className={`text-sm font-semibold truncate ${needsAttention ? 'text-zinc-900' : 'text-zinc-700'}`}>{review.reviewer_name}</span>
            <Stars rating={review.rating} />
          </div>
          <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">{review.review_text || 'No review text'}</p>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${st.color}`}>{st.label}</Badge>
            <span className="text-[10px] text-muted-foreground flex items-center gap-1"><PlatformIcon platform={review.platform} />{PLATFORM_LABELS[review.platform] || review.platform}</span>
            {review.technician_name && <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><User className="w-2.5 h-2.5" />{review.technician_name}</span>}
            {review.job_name && <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Briefcase className="w-2.5 h-2.5" />{review.job_name}</span>}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <span className="text-[10px] text-muted-foreground whitespace-nowrap">{new Date(review.review_date).toLocaleDateString()}</span>
          {isUrgent && <AlertTriangle className="w-3.5 h-3.5 text-red-500 ml-auto mt-1" />}
        </div>
      </div>
    </button>
  );
};

// ── Location Picker Dialog ──
const LocationPickerDialog = ({ open, onClose, oauthState }) => {
  const [step, setStep] = useState('loading'); // loading, accounts, locations, saving, done, error
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState(new Set());
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !oauthState) return;
    setStep('loading');
    setError('');
    marketingAPI.googleAccounts(oauthState)
      .then(res => {
        const { items, error: apiError, status_code, count, raw_body } = res.data;
        // API returned an error
        if (apiError) {
          setError(`${apiError}\n\nHTTP Status: ${status_code || 'unknown'}\nRaw response: ${raw_body || '(empty)'}`);
          setStep('error');
          return;
        }
        setAccounts(items || []);
        if (count === 1 || (items && items.length === 1)) {
          setSelectedAccount(items[0]);
          loadLocations(items[0]);
        } else if (!items || items.length === 0) {
          setError(
            `Google returned HTTP ${status_code} with zero Business accounts.\n\n` +
            `Raw response from Google:\n${raw_body || '(empty)'}\n\n` +
            'Possible causes:\n' +
            '1. The "My Business Account Management API" is not enabled in Google Cloud Console.\n' +
            '2. The API quota is set to 0 (Google default). Request quota increase.\n' +
            '3. The Google Cloud project needs GBP API access approval.'
          );
          setStep('error');
        } else {
          setStep('accounts');
        }
      })
      .catch(err => {
        setError(err?.response?.data?.detail || err?.response?.data?.error || 'Failed to load accounts. OAuth session may have expired.');
        setStep('error');
      });
  }, [open, oauthState]);

  const loadLocations = (account) => {
    setStep('loading');
    marketingAPI.googleLocations(account.name, oauthState)
      .then(res => {
        const { items, error: apiError, count } = res.data;
        if (apiError) {
          setError(`Error loading locations for "${account.account_name}": ${apiError}`);
          setStep('error');
          return;
        }
        setLocations(items || []);
        if (!items || items.length === 0) {
          setError(`No locations found for account "${account.account_name}". The "My Business Business Information API" may need to be enabled, or this account has no managed locations.`);
          setStep('error');
        } else {
          setStep('locations');
        }
      })
      .catch(err => {
        setError(err?.response?.data?.detail || err?.response?.data?.error || 'Failed to load locations.');
        setStep('error');
      });
  };

  const toggleLocation = (locName) => {
    setSelectedLocations(prev => {
      const next = new Set(prev);
      if (next.has(locName)) next.delete(locName);
      else next.add(locName);
      return next;
    });
  };

  const handleSave = async () => {
    if (selectedLocations.size === 0) return;
    setSaving(true);
    try {
      const locsToSave = locations.filter(l => selectedLocations.has(l.name));
      await marketingAPI.googleSaveLocations({
        state: oauthState,
        account_name: selectedAccount.name,
        account_display_name: selectedAccount.account_name,
        locations: locsToSave,
      });
      setStep('done');
      toast.success(`Connected ${locsToSave.length} location(s) successfully!`);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to save locations.');
      setStep('error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(step === 'done'); }}>
      <DialogContent className="sm:max-w-lg" data-testid="location-picker-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-500" />
            Connect Google Business Profile
          </DialogTitle>
          <DialogDescription>
            {step === 'accounts' && 'Select a Google Business account'}
            {step === 'locations' && 'Choose which locations to connect'}
            {step === 'loading' && 'Loading...'}
            {step === 'done' && 'Connection complete!'}
            {step === 'error' && 'Something went wrong'}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 max-h-[400px] overflow-y-auto">
          {step === 'loading' && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              <span className="ml-2 text-sm text-muted-foreground">Discovering your business profiles...</span>
            </div>
          )}

          {step === 'accounts' && (
            <div className="space-y-2">
              {accounts.map(acc => (
                <button
                  key={acc.name}
                  onClick={() => { setSelectedAccount(acc); loadLocations(acc); }}
                  className="w-full text-left p-3 rounded-lg border hover:bg-blue-50 hover:border-blue-200 transition-colors"
                  data-testid={`account-item-${acc.name}`}
                >
                  <p className="font-medium text-sm">{acc.account_name || acc.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Type: {acc.type} · Role: {acc.role}</p>
                </button>
              ))}
            </div>
          )}

          {step === 'locations' && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground mb-2">
                Account: <span className="font-medium text-zinc-700">{selectedAccount?.account_name}</span>
              </p>
              {locations.map(loc => (
                <label
                  key={loc.name}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedLocations.has(loc.name) ? 'bg-blue-50 border-blue-300' : 'hover:bg-zinc-50'}`}
                  data-testid={`location-item-${loc.name}`}
                >
                  <Checkbox
                    checked={selectedLocations.has(loc.name)}
                    onCheckedChange={() => toggleLocation(loc.name)}
                    className="mt-0.5"
                  />
                  <div>
                    <p className="font-medium text-sm">{loc.title}</p>
                    {loc.address && <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" />{loc.address}</p>}
                  </div>
                </label>
              ))}
            </div>
          )}

          {step === 'done' && (
            <div className="text-center py-6">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="font-medium">Locations Connected!</p>
              <p className="text-sm text-muted-foreground mt-1">You can now sync reviews from your connected locations.</p>
            </div>
          )}

          {step === 'error' && (
            <div className="text-center py-6">
              <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <p className="font-medium text-red-700">Connection Error</p>
              <div className="text-sm text-muted-foreground mt-2 text-left bg-red-50 rounded-lg p-3 whitespace-pre-line" data-testid="discovery-error-detail">{error}</div>
            </div>
          )}
        </div>

        <DialogFooter>
          {step === 'locations' && (
            <Button onClick={handleSave} disabled={selectedLocations.size === 0 || saving} data-testid="save-locations-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Connect {selectedLocations.size} Location{selectedLocations.size !== 1 ? 's' : ''}
            </Button>
          )}
          {(step === 'done' || step === 'error') && (
            <Button variant={step === 'done' ? 'default' : 'outline'} onClick={() => onClose(step === 'done')} data-testid="close-location-picker">
              {step === 'done' ? 'Done' : 'Close'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ── Review Detail Panel ──
const ReviewDetail = ({ review, onUpdate, onReply, connections }) => {
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  if (!review) return (
    <div className="flex-1 flex items-center justify-center text-muted-foreground" data-testid="review-detail-empty">
      <div className="text-center">
        <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-30" />
        <p className="text-sm">Select a review to view details</p>
      </div>
    </div>
  );

  const st = STATUS_CONFIG[review.is_archived ? 'archived' : review.status] || STATUS_CONFIG.new;
  const isNegative = review.rating <= 2;
  const hasReply = !!review.reply_text;
  const needsResponse = ['new', 'needs_response'].includes(review.status) && !review.is_archived;
  const isGoogleReview = review.platform === 'google' && review.connection_id;
  const connectedToGoogle = isGoogleReview && connections?.some(c => c.id === review.connection_id && c.is_active);

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      // Try posting to Google first for Google reviews
      if (isGoogleReview) {
        const result = await marketingAPI.googlePostReply(review.id, { reply_text: replyText.trim() });
        if (result.data?.posted_to_google) {
          toast.success('Reply posted to Google and saved');
        } else if (result.data?.error) {
          toast.success('Reply saved locally', { description: result.data.error });
        } else {
          toast.success('Reply saved');
        }
      } else {
        await onReply(review.id, replyText.trim());
        toast.success('Reply saved');
      }
      setReplyText('');
    } catch {
      toast.error('Failed to save reply');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = (newStatus) => {
    onUpdate(review.id, { status: newStatus });
  };

  const handleArchiveToggle = () => {
    onUpdate(review.id, { is_archived: !review.is_archived, status: review.is_archived ? 'new' : review.status });
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden" data-testid="review-detail-panel">
      {/* Negative review banner */}
      {isNegative && !review.is_archived && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-2 flex items-center gap-2" data-testid="negative-review-banner">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <span className="text-xs font-semibold text-red-700">Negative review — needs attention</span>
        </div>
      )}

      {/* Header */}
      <div className={`p-4 border-b space-y-3 ${isNegative && !review.is_archived ? 'bg-red-50/30' : ''}`}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-base">{review.reviewer_name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <Stars rating={review.rating} size="lg" />
              <span className="text-xs text-muted-foreground">{new Date(review.review_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Badge variant="outline" className={`${st.color}`}>{st.label}</Badge>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="review-actions-menu"><ChevronDown className="w-4 h-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {review.status !== 'needs_response' && <DropdownMenuItem onClick={() => handleStatusChange('needs_response')}>Mark Needs Response</DropdownMenuItem>}
                {review.status !== 'flagged' && <DropdownMenuItem onClick={() => handleStatusChange('flagged')}><Flag className="w-3.5 h-3.5 mr-2 text-red-500" />Flag</DropdownMenuItem>}
                {review.status !== 'new' && <DropdownMenuItem onClick={() => handleStatusChange('new')}>Mark as New</DropdownMenuItem>}
                <DropdownMenuItem onClick={handleArchiveToggle}>
                  {review.is_archived ? <><ArchiveRestore className="w-3.5 h-3.5 mr-2" />Unarchive</> : <><Archive className="w-3.5 h-3.5 mr-2" />Archive</>}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">

        {/* Context card */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 bg-zinc-50/70 border rounded-lg p-3 text-xs" data-testid="review-context-card">
          <div>
            <span className="text-muted-foreground">Source</span>
            <p className="font-medium mt-0.5 flex items-center gap-1"><PlatformIcon platform={review.platform} />{PLATFORM_LABELS[review.platform] || review.platform}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Profile / Location</span>
            <p className={`font-medium mt-0.5 ${review.connection_name ? '' : 'text-muted-foreground italic'}`}>{review.connection_name || 'No profile connected'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Customer</span>
            <p className={`font-medium mt-0.5 ${review.customer_name ? '' : 'text-muted-foreground italic'}`}>{review.customer_name || 'Not linked yet'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Job</span>
            <p className={`font-medium mt-0.5 ${review.job_name ? '' : 'text-muted-foreground italic'}`}>{review.job_name || 'Not linked yet'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Technician</span>
            <p className={`font-medium mt-0.5 ${review.technician_name ? '' : 'text-muted-foreground italic'}`}>{review.technician_name || 'Not linked yet'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Status</span>
            <p className="font-medium mt-0.5">{st.label}{review.is_archived ? ' (Archived)' : ''}</p>
          </div>
        </div>

        {/* Customer's review */}
        <div className={`rounded-lg p-4 ${isNegative ? 'bg-red-50/50 border border-red-100' : 'bg-zinc-50'}`} data-testid="review-text-block">
          <p className="text-sm leading-relaxed text-zinc-800">{review.review_text || 'No review text provided.'}</p>
        </div>

        {/* Reply status block */}
        {hasReply ? (
          <div className="bg-blue-50 rounded-lg p-4 border-l-2 border-l-blue-400" data-testid="review-reply-block">
            <div className="flex items-center gap-2 mb-1.5">
              <Send className="w-3 h-3 text-blue-600" />
              <span className="text-xs font-semibold text-blue-800">Reply {connectedToGoogle ? 'Posted to Google' : 'Saved'}</span>
              {review.replied_at && <span className="text-[10px] text-blue-600">{new Date(review.replied_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
              {review.replied_by_name && <span className="text-[10px] text-blue-600">by {review.replied_by_name}</span>}
            </div>
            <p className="text-sm leading-relaxed text-blue-900">{review.reply_text}</p>
            {!connectedToGoogle && isGoogleReview && (
              <p className="text-[10px] text-blue-500 mt-2">Saved locally. Will be posted to Google once connection is active.</p>
            )}
          </div>
        ) : (
          needsResponse && (
            <div className="bg-orange-50 rounded-lg p-3 border border-orange-200 flex items-center gap-2" data-testid="no-reply-banner">
              <Clock className="w-4 h-4 text-orange-500 flex-shrink-0" />
              <span className="text-xs font-medium text-orange-700">No reply yet — this review is awaiting a response.</span>
            </div>
          )
        )}

        {/* Internal notes */}
        {review.internal_notes && (
          <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
            <span className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider">Internal Note</span>
            <p className="text-xs text-amber-800 mt-1">{review.internal_notes}</p>
          </div>
        )}
      </div>

      {/* Reply composer */}
      <div className="border-t p-4 space-y-2" data-testid="review-reply-composer">
        <Label className="text-xs font-medium text-muted-foreground">
          {hasReply ? 'Update Reply' : 'Write Reply'}
          {connectedToGoogle && <span className="ml-1 text-green-600">(will post to Google)</span>}
        </Label>
        <Textarea
          value={replyText}
          onChange={e => setReplyText(e.target.value)}
          placeholder="Write your response to this review..."
          rows={3}
          className="resize-none text-sm"
          data-testid="review-reply-textarea"
        />
        <div className="flex justify-end">
          <Button size="sm" onClick={handleSendReply} disabled={!replyText.trim() || sending} data-testid="review-reply-send">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}
            {hasReply ? 'Update Reply' : connectedToGoogle ? 'Post Reply' : 'Save Reply'}
          </Button>
        </div>
      </div>
    </div>
  );
};


// ── Main Page ──
export default function MarketingReviews() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [connections, setConnections] = useState(null);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRating, setFilterRating] = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterConnection, setFilterConnection] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [sort, setSort] = useState('urgent');
  // OAuth flow
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [oauthState, setOauthState] = useState(null);
  const [connecting, setConnecting] = useState(false);

  // Handle OAuth callback redirect
  useEffect(() => {
    const googleAuth = searchParams.get('google_auth');
    const state = searchParams.get('state');
    const message = searchParams.get('message');

    if (googleAuth === 'success' && state) {
      setOauthState(state);
      setShowLocationPicker(true);
      // Clean URL params
      setSearchParams({}, { replace: true });
    } else if (googleAuth === 'error') {
      toast.error('Google connection failed', { description: message || 'Please try again.' });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadData = useCallback(async () => {
    try {
      const params = { sort, include_archived: includeArchived };
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterRating !== 'all') params.rating = parseInt(filterRating);
      if (filterPlatform !== 'all') params.platform = filterPlatform;
      if (filterConnection !== 'all') params.connection_id = filterConnection;
      if (searchText.trim()) params.search = searchText.trim();

      const [reviewsRes, statsRes, connsRes, statusRes] = await Promise.all([
        marketingAPI.listReviews(params),
        marketingAPI.getReviewStats(),
        marketingAPI.listConnections(),
        marketingAPI.googleStatus(),
      ]);
      setReviews(reviewsRes.data);
      setStats(statsRes.data);
      setConnections(connsRes.data);
      setGoogleStatus(statusRes.data);
    } catch (e) {
      console.error('Failed to load reviews:', e);
      toast.error('Failed to load reviews');
    } finally { setLoading(false); }
  }, [filterStatus, filterRating, filterPlatform, filterConnection, searchText, includeArchived, sort]);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-select first review when list changes
  useEffect(() => {
    if (reviews.length > 0 && (!selectedId || !reviews.find(r => r.id === selectedId))) {
      setSelectedId(reviews[0].id);
    }
  }, [reviews, selectedId]);

  const selected = reviews.find(r => r.id === selectedId) || null;

  const handleUpdate = async (id, data) => {
    try {
      await marketingAPI.updateReview(id, data);
      await loadData();
      toast.success('Review updated');
    } catch { toast.error('Failed to update review'); }
  };

  const handleReply = async (id, text) => {
    await marketingAPI.replyToReview(id, { reply_text: text });
    await loadData();
  };

  const handleConnectGBP = async () => {
    setConnecting(true);
    try {
      const res = await marketingAPI.googleConnect();
      if (res.data.configured && res.data.auth_url) {
        window.location.href = res.data.auth_url;
      } else {
        toast.error('Google not configured', {
          description: res.data.message || 'Contact your administrator to set up Google Cloud credentials.',
          duration: 8000,
        });
      }
    } catch (err) {
      toast.error('Failed to initiate Google connection');
    } finally {
      setConnecting(false);
    }
  };

  const handleSyncNow = async () => {
    const activeConns = connections?.filter(c => c.is_active) || [];
    if (activeConns.length === 0) {
      toast.error('No active connections to sync');
      return;
    }

    setSyncing(true);
    let totalNew = 0;
    let totalUpdated = 0;
    let hasError = false;

    for (const conn of activeConns) {
      // If filtering by connection, only sync that one
      if (filterConnection !== 'all' && conn.id !== filterConnection) continue;
      try {
        const res = await marketingAPI.googleSyncReviews(conn.id);
        if (res.data.success) {
          totalNew += res.data.new_reviews;
          totalUpdated += res.data.updated_reviews;
        } else {
          toast.error(`Sync failed for ${conn.location_name}`, { description: res.data.error });
          hasError = true;
        }
      } catch (err) {
        toast.error(`Sync error for ${conn.location_name}`);
        hasError = true;
      }
    }

    if (!hasError) {
      if (totalNew > 0 || totalUpdated > 0) {
        toast.success(`Sync complete: ${totalNew} new, ${totalUpdated} updated`);
      } else {
        toast.info('Sync complete — no new reviews found');
      }
    }

    await loadData();
    setSyncing(false);
  };

  const handleLocationPickerClose = (wasSuccess) => {
    setShowLocationPicker(false);
    setOauthState(null);
    if (wasSuccess) loadData();
  };

  if (loading) return (
    <Layout><div className="flex items-center justify-center h-[calc(100vh-4rem)]"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div></Layout>
  );

  const hasConnections = connections && connections.length > 0;
  const hasReviews = stats && stats.total_reviews > 0;
  const isGoogleConfigured = googleStatus?.configured;

  // ── State: No connection ──
  if (!hasConnections && !hasReviews) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]" data-testid="reviews-empty-no-connection">
          <div className="max-w-md text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto">
              <Star className="w-8 h-8 text-blue-400" />
            </div>
            <h2 className="text-xl font-bold tracking-tight">Review Management</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Monitor and respond to customer reviews directly from Stock & Load. Reviews are synced automatically from your connected business profiles.
            </p>
            {!isGoogleConfigured && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-left" data-testid="google-not-configured-notice">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-amber-800">Google credentials not configured</p>
                    <p className="text-xs text-amber-600 mt-0.5">
                      To connect your Google Business Profile, an administrator needs to configure Google Cloud OAuth credentials (GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET) in the environment.
                    </p>
                  </div>
                </div>
              </div>
            )}
            <Button onClick={handleConnectGBP} disabled={connecting} data-testid="connect-gbp-btn">
              {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
              Connect Google Business Profile
            </Button>
          </div>
        </div>
        <LocationPickerDialog open={showLocationPicker} onClose={handleLocationPickerClose} oauthState={oauthState} />
      </Layout>
    );
  }

  // ── State: Connected but no reviews synced ──
  if (hasConnections && !hasReviews) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]" data-testid="reviews-empty-no-reviews">
          <div className="max-w-md text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto">
              <RefreshCw className="w-8 h-8 text-green-500" />
            </div>
            <h2 className="text-xl font-bold tracking-tight">No Reviews Yet</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Your Google Business Profile is connected ({connections.map(c => c.location_name).join(', ')}). Click "Sync Reviews" to pull your latest reviews.
            </p>
            <Button onClick={handleSyncNow} disabled={syncing} data-testid="sync-reviews-btn">
              {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              {syncing ? 'Syncing...' : 'Sync Reviews'}
            </Button>
            {!isGoogleConfigured && (
              <p className="text-xs text-amber-600">
                Google credentials are not yet configured. Sync will become available once they are set.
              </p>
            )}
          </div>
        </div>
        <LocationPickerDialog open={showLocationPicker} onClose={handleLocationPickerClose} oauthState={oauthState} />
      </Layout>
    );
  }

  // ── State: Reviews exist ──
  return (
    <Layout>
    <div className="flex flex-col h-[calc(100vh-4rem)]" data-testid="marketing-reviews-page">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 space-y-3 border-b bg-white">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold tracking-tight">Reviews</h1>
          <div className="flex items-center gap-2">
            {hasConnections && connections[0].last_sync_at ? (
              <span className="text-xs text-muted-foreground" data-testid="last-synced">Last synced {(() => { const m = Math.round((Date.now() - new Date(connections[0].last_sync_at).getTime()) / 60000); return m < 1 ? 'just now' : m < 60 ? `${m}m ago` : `${Math.round(m/60)}h ago`; })()}</span>
            ) : hasConnections ? (
              <span className="text-xs text-muted-foreground">Not synced yet</span>
            ) : null}
            <Button variant="outline" size="sm" onClick={handleSyncNow} disabled={syncing} data-testid="sync-now-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Now'}
            </Button>
            <Button variant="outline" size="sm" onClick={handleConnectGBP} disabled={connecting} data-testid="add-location-btn">
              <Link2 className="w-3.5 h-3.5 mr-1" />Add Location
            </Button>
          </div>
        </div>
        <StatCards stats={stats} />

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2" data-testid="review-filters">
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input value={searchText} onChange={e => setSearchText(e.target.value)} placeholder="Search reviews..." className="pl-8 h-8 text-sm" data-testid="review-search" />
          </div>
          {/* Location filter */}
          {hasConnections && connections.length > 1 && (
            <Select value={filterConnection} onValueChange={setFilterConnection}>
              <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="filter-connection"><SelectValue placeholder="Location" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {connections.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.location_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="needs_response">Needs Response</SelectItem>
              <SelectItem value="replied">Replied</SelectItem>
              <SelectItem value="flagged">Flagged</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterRating} onValueChange={setFilterRating}>
            <SelectTrigger className="w-[120px] h-8 text-xs" data-testid="filter-rating"><SelectValue placeholder="Rating" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Ratings</SelectItem>
              <SelectItem value="5">5 Stars</SelectItem>
              <SelectItem value="4">4 Stars</SelectItem>
              <SelectItem value="3">3 Stars</SelectItem>
              <SelectItem value="2">2 Stars</SelectItem>
              <SelectItem value="1">1 Star</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="filter-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="urgent">Urgent First</SelectItem>
              <SelectItem value="newest">Newest First</SelectItem>
              <SelectItem value="oldest">Oldest First</SelectItem>
              <SelectItem value="rating_low">Rating: Low-High</SelectItem>
              <SelectItem value="rating_high">Rating: High-Low</SelectItem>
            </SelectContent>
          </Select>
          <Button variant={includeArchived ? 'secondary' : 'ghost'} size="sm" className="h-8 text-xs" onClick={() => setIncludeArchived(!includeArchived)} data-testid="toggle-archived">
            <Archive className="w-3 h-3 mr-1" />{includeArchived ? 'Hiding Archived' : 'Show Archived'}
          </Button>
        </div>
      </div>

      {/* Split Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Review List */}
        <div className="w-[380px] flex-shrink-0 border-r overflow-y-auto bg-white" data-testid="review-list">
          {reviews.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-6">
              <MessageSquare className="w-8 h-8 mb-2 opacity-30" />
              <p className="text-sm font-medium">No reviews match filters</p>
              <p className="text-xs mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            reviews.map(r => (
              <ReviewListItem key={r.id} review={r} isSelected={r.id === selectedId} onClick={() => setSelectedId(r.id)} />
            ))
          )}
        </div>

        {/* Right: Detail Panel */}
        <ReviewDetail review={selected} onUpdate={handleUpdate} onReply={handleReply} connections={connections} />
      </div>
    </div>
    <LocationPickerDialog open={showLocationPicker} onClose={handleLocationPickerClose} oauthState={oauthState} />
    </Layout>
  );
}
